import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { ref as dbRef, get, push } from "firebase/database";
import { uploadImage } from "../utils/uploadImage";
import { useAuth } from "../context/AuthContext";
import { Calendar, Plus, X, Camera, AlertCircle, MapPin, Clock, Ticket, ArrowLeft } from "lucide-react";

function EventCard({ event }) {
  const waMsg = encodeURIComponent(
    `Hi! I'm interested in the event "${event.title}" on ${event.date} at ${event.venue} that you posted on CampusSwap. Please share details!`
  );
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
      {event.imageUrl && (
        <div className="h-36 overflow-hidden">
          <img src={event.imageUrl} alt={event.title} className="w-full h-full object-cover" />
        </div>
      )}
      {!event.imageUrl && (
        <div className="h-20 bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
          <Calendar size={32} className="text-white opacity-60" />
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <p className="font-bold text-gray-800 text-base line-clamp-1">{event.title}</p>
            <p className="text-xs text-gray-500 mt-0.5">{event.college}</p>
          </div>
          <span className={`text-sm font-bold flex-shrink-0 ${event.ticketPrice === 0 ? "text-green-600" : "text-purple-600"}`}>
            {event.ticketPrice === 0 ? "FREE" : `₹${event.ticketPrice}`}
          </span>
        </div>
        <div className="space-y-1 mt-2">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Calendar size={12} /><span>{event.date} · {event.time}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <MapPin size={12} /><span>{event.venue}</span>
          </div>
          {event.availableTickets > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-green-600">
              <Ticket size={12} /><span>{event.availableTickets} tickets left</span>
            </div>
          )}
        </div>
        {event.description && <p className="text-xs text-gray-500 mt-2 line-clamp-2">{event.description}</p>}
        <a href={`https://wa.me/91${event.whatsapp?.replace(/\D/g, "")}?text=${waMsg}`}
          target="_blank" rel="noreferrer"
          className="mt-3 block text-center bg-purple-600 text-white py-2 rounded-xl text-sm font-semibold hover:bg-purple-700">
          Get Tickets / Info
        </a>
      </div>
    </div>
  );
}

export default function Events() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "", date: "", time: "", venue: "",
    ticketPrice: "0", availableTickets: "", whatsapp: userProfile?.phone || ""
  });

  useEffect(() => { fetchEvents(); }, []);

  async function fetchEvents() {
    setLoading(true);
    try {
      const snap = await get(dbRef(db, "events"));
      if (snap.exists()) {
        const all = Object.entries(snap.val()).map(([id, data]) => ({ id, ...data }));
        setEvents(all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } else {
        setEvents([]);
      }
    } catch (e) {  }
    setLoading(false);
  }

  function handleChange(e) { setForm({ ...form, [e.target.name]: e.target.value }); }

  function handleImage(e) {
    const f = e.target.files[0];
    if (f) { setImageFile(f); setPreview(URL.createObjectURL(f)); }
  }

  async function handlePost(e) {
    e.preventDefault();
    setError("");
    if (!form.title?.trim() || form.title.trim().length < 5) return setError("Event name must be at least 5 characters.");
    if (!form.date.trim() || !form.venue.trim()) return setError("Please enter date and venue.");
    if (!form.whatsapp.trim() || !/^[6-9]\d{9}$/.test(form.whatsapp.replace(/\s/g, "")))
      return setError("Enter a valid 10-digit WhatsApp number.");
    setPosting(true);
    try {
      let imageUrl = null;
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }
      await push(dbRef(db, "events"), {
        title: form.title.trim(),
        description: form.description.trim(),
        date: form.date.trim(),
        time: form.time.trim(),
        venue: form.venue.trim(),
        ticketPrice: Number(form.ticketPrice) || 0,
        availableTickets: Number(form.availableTickets) || 0,
        whatsapp: form.whatsapp.trim(),
        imageUrl,
        postedBy: currentUser.uid,
        organizer: userProfile?.name || "Student",
        college: userProfile?.college || "",
        createdAt: new Date().toISOString(),
      });
      setForm({ title: "", description: "", date: "", time: "", venue: "", ticketPrice: "0", availableTickets: "", whatsapp: userProfile?.phone || "" });
      setImageFile(null); setPreview(null);
      setShowForm(false);
      fetchEvents();
    } catch (err) { setError("Failed to post. Try again."); }
    setPosting(false);
  }

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-4 pt-4 pb-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 rounded-full hover:bg-white/20"><ArrowLeft size={20} /></button>
            <div>
              <h1 className="text-xl font-bold">Campus Events</h1>
              <p className="text-purple-100 text-xs mt-0.5">Fests, workshops, competitions & more</p>
            </div>
          </div>
          <button onClick={() => setShowForm(true)}
            className="bg-white text-purple-700 px-3 py-1.5 rounded-xl font-semibold text-sm flex items-center gap-1">
            <Plus size={16} /> Add Event
          </button>
        </div>
      </div>

      <div className="px-4 mt-4 space-y-4">
        {loading ? (
          [1,2].map(i => <div key={i} className="h-56 bg-white rounded-xl animate-pulse" />)
        ) : events.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl">
            <Calendar size={48} className="mx-auto mb-3 text-gray-200" />
            <p className="text-gray-500 font-medium">No events posted yet</p>
            <button onClick={() => setShowForm(true)} className="mt-4 bg-purple-600 text-white px-6 py-2.5 rounded-xl font-semibold text-sm">
              Add First Event
            </button>
          </div>
        ) : events.map(e => <EventCard key={e.id} event={e} />)}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-end">
          <div className="bg-white rounded-t-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-4 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-800 text-lg">Add an Event</h3>
              <button onClick={() => { setShowForm(false); setError(""); }}><X size={24} /></button>
            </div>
            <form onSubmit={handlePost} className="p-4 space-y-3 pb-24">
              {error && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm"><AlertCircle size={16} />{error}</div>}

              <div onClick={() => document.getElementById("event-img").click()}
                className="border-2 border-dashed border-purple-300 rounded-xl h-32 flex items-center justify-center cursor-pointer overflow-hidden">
                {preview ? <img src={preview} alt="p" className="h-full w-full object-cover" />
                  : <div className="text-purple-400 text-center"><Camera size={24} className="mx-auto mb-1" /><p className="text-xs">Event banner / poster (optional)</p></div>}
              </div>
              <input id="event-img" type="file" accept="image/*" onChange={handleImage} className="hidden" />

              <input name="title" value={form.title} onChange={handleChange} required
                placeholder="Event name (e.g. TechFest 2024, Coding Marathon)"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-purple-400" />

              <textarea name="description" value={form.description} onChange={handleChange} rows={2}
                placeholder="What's happening? Who can attend?"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-purple-400 resize-none" />

              <div className="grid grid-cols-2 gap-3">
                <input name="date" value={form.date} onChange={handleChange} required
                  placeholder="Date (e.g. 25 March)"
                  className="border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-purple-400" />
                <input name="time" value={form.time} onChange={handleChange}
                  placeholder="Time (e.g. 10:00 AM)"
                  className="border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-purple-400" />
              </div>

              <input name="venue" value={form.venue} onChange={handleChange} required
                placeholder="Venue (e.g. Main Auditorium, Online)"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-purple-400" />

              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">₹</span>
                  <input name="ticketPrice" type="number" min="0" value={form.ticketPrice} onChange={handleChange}
                    placeholder="Ticket price"
                    className="w-full border border-gray-200 rounded-xl pl-7 pr-4 py-3 text-sm focus:ring-2 focus:ring-purple-400" />
                </div>
                <input name="availableTickets" type="number" min="0" value={form.availableTickets} onChange={handleChange}
                  placeholder="Tickets available"
                  className="border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-purple-400" />
              </div>

              <input name="whatsapp" type="tel" value={form.whatsapp} onChange={handleChange} required
                placeholder="WhatsApp for inquiries *"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-purple-400" />

              <button type="submit" disabled={posting}
                className="w-full bg-purple-600 text-white py-3.5 rounded-xl font-semibold hover:bg-purple-700 disabled:opacity-50">
                {posting ? "Posting..." : "Post Event"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
